/**
 * English translations for User Guide feature
 */
export default {
  // Help Center
  helpCenter: {
    title: "Help Center",
    search: "Search for help...",
    gettingStarted: "Getting Started",
    featureGuides: "Feature Guides",
    quickReference: "Quick Reference",
    contactSupport: "Contact Support",
    noResults: "No results found",
    allGuidesComplete: "All guides completed!",
    progress: "Progress",
    resetProgress: "Reset Progress",
    resetConfirm: "Are you sure you want to reset all guide progress?",
  },

  // Onboarding Welcome
  onboarding: {
    welcome: {
      title: "Welcome to the system!",
      subtitle: "Let's get you started with a quick tour of the main features",
      startTour: "Start Guided Tour",
      exploreLater: "I'll explore on my own",
      dontShowAgain: "Don't show this again",
    },
    progress: {
      title: "Your Progress",
      completed: "{{completed}} of {{total}} completed",
    },
    checklist: {
      title: "Getting Started Checklist",
      viewDashboard: "View the dashboard",
      manageRecords: "Create or edit a record",
      completeTask: "Complete a workflow task",
      importData: "Import data from a file",
      customizeTable: "Customize table columns",
    },
  },

  // Page Tour Button
  pageTour: {
    startTooltip: "View guide for this page",
    replayTooltip: "Replay guide for this page",
  },

  // Tour Definitions
  tours: {
    // Common messages
    common: {
      elementsNotFound: "Guide elements not found on this page",
      completed: "Guide completed!",
    },

    // Workflow/InBasket Tour
    workflowInbasket: {
      title: "Task Management",
      description: "Learn how to view and complete workflow tasks",
      steps: {
        taskList: {
          title: "Task List",
          description:
            "This is your task inbox. All pending tasks assigned to you appear here. You can see task details, source items, and deadlines.",
        },
        viewModes: {
          title: "View Modes",
          description:
            "Switch between table view and card view using these buttons. Card view is more visual, while table view shows more details at once.",
        },
        filters: {
          title: "Filter Tasks",
          description:
            "Use filters to find specific tasks. You can filter by task type, source item, or date range.",
        },
        completeTask: {
          title: "Complete a Task",
          description:
            "Click the 'Complete' button to open the task completion dialog. This is where you'll make your decision.",
        },
        selectPath: {
          title: "Select a Path",
          description:
            "Choose the appropriate workflow path. Each path represents a different decision (e.g., Approve, Reject, Request Changes).",
        },
        addComments: {
          title: "Add Comments",
          description:
            "Optionally add comments to explain your decision. Comments are visible to other users in the workflow history.",
        },
      },
    },

    // Entity Management Tour
    entityManagement: {
      title: "Managing Records",
      description:
        "Learn how to create, view, and edit records like Leads, Customers, and Opportunities",
      steps: {
        navigation: {
          title: "Navigation",
          description:
            "Use the sidebar to navigate between different record types. Click on any item type to see its list.",
        },
        listView: {
          title: "List View",
          description:
            "Browse all records in a sortable, filterable table. Click on column headers to sort, or use the search box to find specific records.",
        },
        createNew: {
          title: "Create New Record",
          description:
            "Click the 'Create' button to add a new record. A form will open where you can enter the details.",
        },
        importData: {
          title: "Import Data from Excel",
          description:
            "Click 'Import Data' to bulk import records from Excel or CSV files. Download the template, fill in your data, and upload.",
        },
        fillForm: {
          title: "Fill the Form",
          description:
            "Enter the required information in the form fields. Required fields are marked with a red asterisk (*). Sections help organize related fields.",
        },
        viewDetails: {
          title: "View Details",
          description:
            "Click on any record in the list to see its full details, including related items and history.",
        },
        editRecord: {
          title: "Edit Record",
          description:
            "Click the 'Edit' button to modify the record's information. Don't forget to save your changes!",
        },
        relatedItems: {
          title: "Related Items",
          description:
            "View and manage items related to this record using the tabs at the bottom. You can add new relationships or navigate to related records.",
        },
      },
    },

    // Import Wizard Tour
    importWizard: {
      title: "Data Import",
      description: "Learn how to import data from Excel or CSV files",
      steps: {
        openImport: {
          title: "Open Import Wizard",
          description:
            "Click the 'Import' button on any list page to start importing data. You can import up to 1000 records at a time.",
        },
        downloadTemplate: {
          title: "Download Template",
          description:
            "Download the template file to see the correct format. Fill in your data following the template structure.",
        },
        uploadFile: {
          title: "Upload File",
          description:
            "Drag and drop your file here or click to browse. Supported formats: Excel (.xlsx) and CSV files.",
        },
        mapColumns: {
          title: "Map Columns",
          description:
            "Match your file columns to system fields. The system will try to auto-match based on column names. Required fields are marked with *.",
        },
        previewData: {
          title: "Preview Data",
          description:
            "Review your data before importing. Invalid rows are highlighted in red with error messages. Fix any issues before proceeding.",
        },
        importResults: {
          title: "Import Results",
          description:
            "View the import summary showing successful and failed records. Download the error report to fix and re-import failed rows.",
        },
      },
    },

    // Lifecycle States Tour
    lifecycleStates: {
      title: "Lifecycle States",
      description: "Understand how records progress through different states",
      steps: {
        stateDisplay: {
          title: "State Progress",
          description:
            "The lifecycle display shows the current state and progress. Green checkmarks indicate completed states, the highlighted state is current.",
        },
        currentState: {
          title: "Current State",
          description:
            "Each state has specific rules about what actions are available and what data can be modified. Some fields may be locked in certain states.",
        },
        promoteAction: {
          title: "Promote to Next State",
          description:
            "When available, click the 'Promote' button to move the record to the next state. This may trigger workflow tasks or notifications.",
        },
        stateHistory: {
          title: "State History",
          description:
            "View the complete history of state changes, including who made each change and when.",
        },
      },
    },

    // Column Management Tour
    columnManagement: {
      title: "Table Customization",
      description: "Learn how to customize table columns and views",
      steps: {
        viewToggle: {
          title: "View Toggle",
          description:
            "Switch between table view (detailed rows) and card view (visual cards) using these buttons.",
        },
        columnSettings: {
          title: "Column Settings",
          description:
            "Click the settings icon to open column configuration. Here you can show, hide, or reorder columns.",
        },
        toggleColumns: {
          title: "Show/Hide Columns",
          description:
            "Check or uncheck columns to show or hide them. Your preferences are saved automatically.",
        },
        columnOrder: {
          title: "Reorder Columns",
          description:
            "Drag and drop columns to change their order. The new order will be remembered for your next visit.",
        },
        resetDefault: {
          title: "Reset to Default",
          description:
            "Click 'Reset' to restore the original column configuration if you want to start over.",
        },
      },
    },

    // Dashboard Overview Tour
    dashboardOverview: {
      title: "Dashboard Overview",
      description: "Learn how to read and use the dashboard metrics",
      steps: {
        kpiCards: {
          title: "Key Metrics",
          description:
            "These cards show your key performance indicators at a glance. Click on any card to see more details.",
        },
        charts: {
          title: "Charts & Graphs",
          description:
            "Visual representations of your data. Hover over chart elements to see specific values.",
        },
        pendingTasks: {
          title: "Pending Tasks",
          description:
            "Tasks waiting for your attention. Click on a task to open it, or click 'View All' to see your full inbox.",
        },
      },
    },
  },

  // Contextual Help
  contextualHelp: {
    lifecycle: {
      title: "About Lifecycle States",
      content:
        "Records progress through defined states from start to finish. Each state may have different rules about what actions are available.",
    },
    workflow: {
      title: "About Workflows",
      content:
        "Workflows automate business processes by creating tasks and routing work to the right people. Complete tasks to move items forward.",
    },
    import: {
      title: "About Data Import",
      content:
        "Import data from Excel or CSV files. Download a template first to ensure your data is in the correct format.",
    },
    relationships: {
      title: "About Related Items",
      content:
        "Records can be linked to other records. Use relationship tabs to view and manage these connections.",
    },
    columns: {
      title: "About Column Settings",
      content:
        "Customize which columns appear in the table. Your preferences are saved and will be remembered on your next visit.",
    },
  },

  // Actions
  actions: {
    startTour: "Start Tour",
    skipTour: "Skip",
    nextStep: "Next",
    previousStep: "Back",
    finish: "Finish",
    restartTour: "Restart Tour",
    gotIt: "Got it!",
    close: "Close",
    learnMore: "Learn More",
  },

  // Categories
  categories: {
    essential: {
      title: "Essential Features",
      description: "Core features you'll use every day",
    },
    dataManagement: {
      title: "Data Management",
      description: "Working with records and data",
    },
    workflow: {
      title: "Workflow & Tasks",
      description: "Managing business processes",
    },
    customization: {
      title: "Customization",
      description: "Personalizing your workspace",
    },
  },
};
